import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ThemeToggle from '../../../components/ui/ThemeToggle';

const AccountSettings = ({ onExportData, onDeleteAccount }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { theme, isDark } = useTheme();

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await onExportData();
      alert('Your data export has been sent to your email address.');
    } catch (error) {
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm account deletion.');
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteAccount();
    } catch (error) {
      alert('Failed to delete account. Please contact support.');
      setIsDeleting(false);
    }
  };

  const accountActions = [
    {
      id: 'export',
      title: 'Export Account Data',
      description: 'Download a copy of all your account data and activity',
      icon: 'Download',
      action: handleExportData,
      loading: isExporting,
      variant: 'outline'
    },
    {
      id: 'privacy',
      title: 'Privacy Settings',
      description: 'Manage your privacy preferences and data sharing',
      icon: 'Shield',
      action: () => alert('Privacy settings coming soon'),
      variant: 'outline'
    },
    {
      id: 'security',
      title: 'Security Center',
      description: 'Review login activity and security settings',
      icon: 'Lock',
      action: () => alert('Security center coming soon'),
      variant: 'outline'
    }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
          <Icon name="Settings" size={20} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Account Settings</h2>
          <p className="text-sm text-muted-foreground">Manage your account and preferences</p>
        </div>
      </div>

      {/* Theme Settings */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Palette" size={20} className="text-primary" />
          <span>Appearance</span>
        </h3>
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name={isDark ? 'Moon' : 'Sun'} size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">Theme Preference</h4>
                <p className="text-sm text-muted-foreground">
                  Currently using {isDark ? 'dark' : 'light'} mode. Switch between light and dark themes.
                </p>
              </div>
            </div>
            <ThemeToggle variant="outline" size="sm" showLabel={true} />
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="space-y-4 mb-8">
        {accountActions?.map((action) => (
          <div
            key={action?.id}
            className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                <Icon name={action?.icon} size={20} className="text-muted-foreground" />
              </div>
              <div>
                <h3 className="font-medium text-foreground">{action?.title}</h3>
                <p className="text-sm text-muted-foreground">{action?.description}</p>
              </div>
            </div>
            <Button
              variant={action?.variant}
              size="sm"
              onClick={action?.action}
              loading={action?.loading}
            >
              {action?.id === 'export' ? 'Export' : 'Manage'}
            </Button>
          </div>
        ))}
      </div>

      {/* Account Statistics */}
      <div className="bg-muted/50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-foreground mb-3">Account Statistics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-foreground">12</p>
            <p className="text-xs text-muted-foreground">Events Attended</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">3,450</p>
            <p className="text-xs text-muted-foreground">Points Earned</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">8</p>
            <p className="text-xs text-muted-foreground">Venues Visited</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">2</p>
            <p className="text-xs text-muted-foreground">Years Member</p>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="border border-error/20 rounded-lg p-4 bg-error/5">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="AlertTriangle" size={20} className="text-error" />
          <h3 className="font-medium text-error">Danger Zone</h3>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>

        {!showDeleteConfirm ? (
          <Button
            variant="destructive"
            size="sm"
            iconName="Trash2"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Account
          </Button>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Type "DELETE" to confirm account deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-input text-foreground"
                placeholder="Type DELETE here"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAccount}
                loading={isDeleting}
                disabled={deleteConfirmText !== 'DELETE'}
                iconName="Trash2"
                className="flex-1"
              >
                Permanently Delete Account
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">This action will:</p>
              <ul className="space-y-1">
                <li>• Permanently delete your account and profile</li>
                <li>• Remove all your tickets and order history</li>
                <li>• Delete your points and rewards</li>
                <li>• Cancel any upcoming event registrations</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* GDPR Compliance Notice */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-1">Data Protection</p>
            <p>
              We comply with GDPR and other data protection regulations. You have the right to 
              access, correct, or delete your personal data at any time. For more information, 
              please review our Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;